import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-label", "checked", "onChange", "disabled", "size", "percentFilled", "readOnly", "tabIndex", "id", "onMouseEnter", "onMouseLeave", "IconComponent", "active", "focus", "hover", "index"];
import styled from 'foundations-theming/utils';
import { DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE } from '../../constants';
import { Checkable } from '../Checkable/Checkable';
import { useState } from 'react';
import { useIconSize } from 'foundations-assets/icons/useIconSize';
import { useIsKeyboardMode } from '../../../components/AppProvider/KeyboardModeProvider';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { MERCURY_LAYER } from '../../constants';
import { getScreenReaderOnlyStyles } from '../../utils/styles/styles';
import { getFocusStyleObject } from '../../utils/focus/focus.style';
import { FavoriteIcon } from 'foundations-assets/icons/components/FavoriteIcon';
import { FavoriteHollowIcon } from 'foundations-assets/icons/components/FavoriteHollowIcon';

/*
  If there is a percentage, the background icon will be the unchecked icon
  for the foreground icon to cover
*/
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ForegroundIconContainer = styled.div.withConfig({
  displayName: "IconToggle__ForegroundIconContainer"
})(({
  $percentFilled,
  $size
}) => {
  return {
    left: '0px',
    // This should be hard-coded rather than tokenized
    overflow: 'hidden',
    position: 'absolute',
    zIndex: MERCURY_LAYER,
    /*
     * This calculation is slightly off because the icons have a bit of padding on all 4 sides of the viewbox.
     * This means that percentages <50% will be filled slighly less than expected and percentages >50% will be filled slightly more than expected.
     * Because the calculation is only off by just a couple of pixels, it shouldn't really be that noticeable, but we can fix it in the future if needed.
     * Bug report: https://github.com/HubSpotEngineering/foundations-components/issues/1535
     */
    inlineSize: `${$percentFilled / 100 * parseInt(useIconSize($size), 10)}px`
  };
});
const StyledHiddenInput = styled.input.withConfig({
  displayName: "IconToggle__StyledHiddenInput"
})(({
  theme
}) => {
  return Object.assign({}, getScreenReaderOnlyStyles(theme));
});
const StyledIconToggle = styled.label.withConfig({
  displayName: "IconToggle__StyledIconToggle"
})(({
  $disabled,
  $readOnly,
  $cssStyles
}) => {
  return Object.assign({
    display: 'inline-flex',
    position: 'relative',
    cursor: $disabled || $readOnly ? 'inherit' : 'pointer',
    lineHeight: '117%'
  }, $cssStyles);
});
const StyledFocusContainer = styled.span.withConfig({
  displayName: "IconToggle__StyledFocusContainer"
})(({
  $isFocused,
  theme
}) => {
  const isKeyboardMode = useIsKeyboardMode();
  return Object.assign({}, $isFocused && isKeyboardMode ? getFocusStyleObject({
    theme,
    unwrapped: true
  }) : undefined);
});
export const DefaultIconComponent = ({
  checked,
  percentFilled,
  size,
  isForeground
}) => {
  const backgroundIconUnchecked = !checked || percentFilled != null;
  const Icon = !isForeground && backgroundIconUnchecked ? FavoriteHollowIcon : FavoriteIcon;
  const iconColor = !isForeground && backgroundIconUnchecked ? 'icon-core-disabled' : 'fill-accent-yellow-default'; // no icon-* token avavilable for accent yellow color

  return /*#__PURE__*/_jsx(Icon, {
    color: iconColor,
    size: size
  });
};
export const InternalIconToggle = _ref => {
  let {
      'aria-label': ariaLabel,
      checked = false,
      onChange,
      disabled = false,
      size = '600',
      percentFilled,
      readOnly = false,
      tabIndex = 0,
      id,
      onMouseEnter,
      onMouseLeave,
      IconComponent = DefaultIconComponent,
      active,
      focus,
      hover,
      index
    } = _ref,
    restProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    tokenProps,
    domProps
  } = filterProps(restProps);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const iconComponentSharedProps = {
    active,
    checked,
    disabled,
    focus,
    hover,
    index,
    percentFilled,
    readOnly,
    size
  };
  return /*#__PURE__*/_jsxs(StyledIconToggle, Object.assign({
    $disabled: disabled,
    $readOnly: readOnly,
    $cssStyles: cssStyles,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave
  }, domProps, {
    [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: 'IconToggle',
    children: [/*#__PURE__*/_jsx(StyledHiddenInput, {
      "aria-label": ariaLabel,
      checked: checked,
      id: id,
      type: "checkbox",
      onChange: evt => {
        if (disabled || readOnly) {
          evt.preventDefault();
          return;
        }
        onChange === null || onChange === void 0 || onChange(evt);
      },
      tabIndex: tabIndex,
      onFocus: () => setIsInputFocused(true),
      onBlur: () => setIsInputFocused(false)
    }), /*#__PURE__*/_jsx(StyledFocusContainer, {
      $isFocused: isInputFocused,
      children: /*#__PURE__*/_jsx(IconComponent, Object.assign({}, iconComponentSharedProps, {
        isForeground: false
      }))
    }), percentFilled != null && /*#__PURE__*/_jsx(ForegroundIconContainer, {
      $percentFilled: percentFilled,
      $size: size,
      children: /*#__PURE__*/_jsx(IconComponent, Object.assign({}, iconComponentSharedProps, {
        isForeground: true
      }))
    })]
  }));
};
export const IconToggle = Checkable(InternalIconToggle);
IconToggle.displayName = 'IconToggle';